import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import DotGrid from '../components/common/DotGrid';
import codeEvaluationApi, { Problem, ProblemsResponse, ProblemStatsResponse } from '../api/codeEvaluationApi';
import { vibeService } from '../services/VibeService';
import './CandidateProfile.css';
import './CodingChallenge.css';

const CodingChallenge: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  
  // API state
  const [problems, setProblems] = useState<Problem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProblems, setTotalProblems] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  
  // Filter state
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  
  // Stats for filter dropdowns
  const [stats, setStats] = useState<ProblemStatsResponse | null>(null);

  // Get user initials
  const initials = useMemo(() => {
    const email = user?.email || '';
    const name = email.split('@')[0] || 'U';
    const parts = name.split(/[._]/).filter(Boolean);
    const first = (parts[0] || 'U')[0] || 'U';
    const second = (parts[1] || parts[0] || 'U')[0] || 'U';
    return `${first}${second}`.toUpperCase();
  }, [user?.email]);

  // Fetch stats for filter dropdowns on mount
  useEffect(() => {
    codeEvaluationApi.getStats()
      .then(setStats)
      .catch((e) => console.error('Failed to load stats:', e));
  }, []);

  // Fetch problems when page or filters change
  const fetchProblems = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: ProblemsResponse = await codeEvaluationApi.getProblems({
        page,
        limit: 10,
        language: languageFilter || undefined,
        difficulty: difficultyFilter as 'easy' | 'medium' | 'hard' | undefined,
        category: categoryFilter || undefined,
      });
      
      setProblems(response.problems);
      setTotalPages(response.total_pages);
      setTotalProblems(response.total_problems);
      setHasNext(response.has_next);
      setHasPrevious(response.has_previous);
    } catch (e: any) {
      const message = e?.response?.data?.detail || e?.message || 'Failed to load problems';
      setError(message);
      setProblems([]);
      vibeService.trigger('error');
    } finally {
      setLoading(false);
    }
  }, [page, languageFilter, difficultyFilter, categoryFilter]);

  useEffect(() => {
    fetchProblems();
  }, [fetchProblems]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [languageFilter, difficultyFilter, categoryFilter]);

  const handleStartChallenge = (problem: Problem, mode: 'timed' | 'practice') => {
    navigate('/coding-interview', {
      state: {
        problemId: problem.id,
        language: problem.language,
        mode,
      },
    });
  };

  // Difficulty badge class helper
  const getDifficultyClass = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'difficulty-easy';
      case 'medium': return 'difficulty-medium';
      case 'hard': return 'difficulty-hard';
      default: return '';
    }
  };

  return (
    <div className="candidate-profile">
      {/* Background */}
      <div className="aurora-background">
        <DotGrid />
      </div>
      <div className="hero-glow" />

      {/* Floating Luci brand logo */}
      <div className="candidate-logo-floating" onClick={() => navigate('/candidate')} style={{ cursor: 'pointer' }}>
        <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
        <div className="logo-text">
          <div className="logo-title">Hyper</div>
          <div className="logo-subtitle">Career assistant</div>
        </div>
      </div>

      {/* Header */}
      <div className="profile-header">
        <div className="header-left"></div>
        <div className="header-center">
          <h1 style={{ margin: 0, color: '#e6eef8', fontSize: 18, fontWeight: 600 }}>Coding Challenges</h1>
        </div>
        <div className="header-right">
          <div className="user-avatar-small" style={{ cursor: 'default' }}>
            {initials}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-content">
        <div className="profile-main-content">
          {/* Filter Bar */}
          <div className="challenge-filters">
            <div className="challenge-filters-left">
              <select 
                className="challenge-filter-select"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
              >
                <option value="">All Languages</option>
                {stats?.languages.map(lang => (
                  <option key={lang} value={lang}>{lang.charAt(0).toUpperCase() + lang.slice(1)}</option>
                ))}
              </select>
              
              <select 
                className="challenge-filter-select"
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              
              <select 
                className="challenge-filter-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {stats?.categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="challenge-filters-right">
              <span className="challenge-count-badge">
                {totalProblems} problem{totalProblems !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="challenge-loading">
              <div className="challenge-spinner"></div>
              <span>Loading problems...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="challenge-error">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
              <button className="challenge-retry-btn" onClick={fetchProblems}>
                Retry
              </button>
            </div>
          )}

          {/* Challenge List */}
          {!loading && !error && problems.length > 0 && (
            <div className="challenge-list">
              {problems.map((problem) => (
                <div key={`${problem.language}-${problem.id}`} className="challenge-card">
                  <div className="challenge-card-content">
                    <div className="challenge-card-header">
                      <div className="challenge-card-title">{problem.title}</div>
                      <div className="challenge-card-badges">
                        <span className={`challenge-difficulty-badge ${getDifficultyClass(problem.difficulty)}`}>
                          {problem.difficulty}
                        </span>
                        <span className="challenge-language-badge">{problem.language}</span>
                        <span className="challenge-category-badge">{problem.category}</span>
                      </div>
                    </div>
                    <div className="challenge-card-meta">
                      <span className="challenge-test-count">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 11 12 14 22 4" />
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        {problem.test_case_count} test cases
                      </span>
                    </div>
                  </div>
                  <div className="challenge-card-actions">
                    <button 
                      className="challenge-action-btn timed"
                      onClick={() => handleStartChallenge(problem, 'timed')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      Timed
                    </button>
                    <button 
                      className="challenge-action-btn practice"
                      onClick={() => handleStartChallenge(problem, 'practice')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                      </svg>
                      Practice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && problems.length === 0 && (
            <div className="challenge-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              <span>No problems found</span>
              <p>Try adjusting your filters or check back later.</p>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && !error && totalPages > 1 && (
            <div className="challenge-pagination">
              <button 
                className="pagination-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!hasPrevious}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Previous
              </button>
              
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              
              <button 
                className="pagination-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={!hasNext}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodingChallenge;
